import {
  Close,
  ExpandLess,
  ExpandMore,
  Person,
  Timeline,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatDate, getChileUtcOffset } from 'lib/functions/utils';
import React, { useState } from 'react';

const chileOffsetInMs = getChileUtcOffset() * 60 * 60 * 1000;

interface EvaluationHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  riskData: any;
}

const EvaluationHistoryDialog: React.FC<EvaluationHistoryDialogProps> = ({
  open,
  onClose,
  riskData,
}) => {
  const [expandedEvaluation, setExpandedEvaluation] = useState<number | null>(
    null,
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aprobado':
        return 'success';
      case 'Rechazado':
        return 'error';
      default:
        return 'warning';
    }
  };

  const renderNodeTooltip = (node: any) => (
    <Box sx={{ p: 2, maxWidth: '320px', minWidth: '250px' }}>
      {node.node && node.node !== '' && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: 'text.primary',
              fontSize: '1rem',
            }}
          >
            {node.node}
          </Typography>
        </Box>
      )}

      {node.resultText && node.resultText !== '' && (
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            p: 1.5,
            borderRadius: 1.5,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.5,
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {node.resultText}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (!riskData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
          width: '700px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              p: 0.75,
              borderRadius: '50%',
            }}
          >
            <Timeline sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Historial de Evaluaciones
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {riskData.identifier}
              </Typography>
              <Chip
                label={`${riskData.evaluations?.length || 0} evaluaciones`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {riskData.evaluations?.map((evaluation: any, index: number) => (
          <Card
            key={index}
            sx={{
              border: '1px solid #e0e0e0',
              mb: 2,
              boxShadow: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#fafafa',
              },
            }}
            onClick={() =>
              setExpandedEvaluation(expandedEvaluation === index ? null : index)
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.7rem', color: 'secondary.main' }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                    >
                      {evaluation.reviewerName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {formatDate(
                        new Date(evaluation.timestamp + chileOffsetInMs),
                        'large',
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={evaluation.status}
                    color={getStatusColor(evaluation.status) as any}
                    size="small"
                    sx={{ height: '25px', fontSize: '0.7rem' }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                    }}
                  >
                    {expandedEvaluation === index ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </Box>
                </Box>
              </Box>

              <Collapse in={expandedEvaluation === index}>
                <Divider sx={{ my: 1.5 }} />
                {evaluation.revisionStory && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}
                    >
                      Nodos de Evaluación
                    </Typography>

                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {evaluation.revisionStory.map(
                        (node: any, nodeIndex: number) => (
                          <LightTooltip
                            key={nodeIndex}
                            title={renderNodeTooltip(node)}
                            placement="right"
                          >
                            <Box
                              sx={{
                                p: 1,
                                border: '1px solid #e8e8e8',
                                borderRadius: 1,
                                backgroundColor: '#fafafa',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#f0f0f0',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.6rem' }}
                                  >
                                    {nodeIndex + 1}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {!node.node || node.node === ''
                                      ? 'Sin comentarios'
                                      : node.node}
                                  </Typography>
                                  {node.resultText &&
                                    node.resultText !== '' && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: '0.65rem',
                                          fontStyle: 'italic',
                                          color: '#666666',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flexShrink: 0,
                                          maxWidth: '275px',
                                          minWidth: '80px',
                                        }}
                                      >
                                        {node.resultText}
                                      </Typography>
                                    )}
                                </Box>
                                {evaluation.engineType !== 'Árbol' && (
                                  <Chip
                                    label={node.comitee}
                                    color={getStatusColor(node.comitee) as any}
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '0.6rem',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </LightTooltip>
                        ),
                      )}
                    </Box>
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationHistoryDialog;
